'use client'

import * as React from 'react'
import { useEffect, useState } from 'react'
import { GiveToGetPracticingModal } from './give-to-get-practicing-modal'
import { GiveToGetNonPracticingModal } from './give-to-get-non-practicing-modal'
import { useSuspenseQuery } from '@apollo/client'
import { GET_USER_PROFILE } from '@/modules/user/profile/queries'
import { Query } from '@/lib/generated/graphql-types'
import { OnboardingAccountType } from '@/modules/surveys/configs/onboarding/constants'
import { WorkStatus } from '@/modules/surveys/configs/onboarding/personal-info-config'
import { GiveToGetStudentModal } from './give-to-get-student-modal'
import { EmployerReviewModal } from './employer-review-modal'
import { usePostHog } from 'posthog-js/react'

export function GiveToGetModalManager({
  didComeFromOnboarding,
  isAuthenticated,
}: {
  didComeFromOnboarding: boolean
  isAuthenticated: boolean
}): React.JSX.Element {
  const posthog = usePostHog()
  const { data: userData } = useSuspenseQuery<Pick<Query, 'getUserProfile'>>(
    GET_USER_PROFILE,
    { skip: !isAuthenticated },
  )
  const onboardingType = userData?.getUserProfile?.onboardingType
  const workStatus = userData?.getUserProfile?.workStatus
  const [isGiveToGetModalOpen, setIsGiveToGetModalOpen] = useState(
    Boolean(didComeFromOnboarding && onboardingType),
  )
  const onGiveToGetModalClose = () => {
    setIsGiveToGetModalOpen(false)
  }
  const isPhysician = onboardingType === OnboardingAccountType.Physician
  const isApp = onboardingType === OnboardingAccountType.AdvancedPractice
  const isResident = workStatus === WorkStatus.Resident
  const isFellow = workStatus === WorkStatus.Fellow
  const isInTraining = workStatus === WorkStatus.InTraining
  const isStudent = onboardingType === OnboardingAccountType.Student
  const isEmployer = onboardingType === OnboardingAccountType.Employer
  const isPracticing = !(isResident || isFellow || isInTraining)

  useEffect(() => {
    // trigger welcome modal if one of the modals are shown below
    if (isGiveToGetModalOpen && onboardingType) {
      posthog.capture('welcome_modal', {
        onboarding_type: onboardingType,
        work_status: workStatus,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGiveToGetModalOpen, onboardingType])
  if (!isAuthenticated) {
    return <></>
  }

  return (
    <>
      <GiveToGetPracticingModal
        isOpen={isGiveToGetModalOpen && (isPhysician || isApp) && isPracticing}
        onClose={onGiveToGetModalClose}
        isAuthenticated={isAuthenticated}
      />
      <GiveToGetNonPracticingModal
        isOpen={isGiveToGetModalOpen && (isPhysician || isApp) && !isPracticing}
        onClose={onGiveToGetModalClose}
        isAuthenticated={isAuthenticated}
      />
      <GiveToGetStudentModal
        isOpen={isGiveToGetModalOpen && isStudent}
        onClose={onGiveToGetModalClose}
        isAuthenticated={isAuthenticated}
      />
      <EmployerReviewModal
        isOpen={isGiveToGetModalOpen && isEmployer}
        onClose={onGiveToGetModalClose}
      />
    </>
  )
}
